import React, { useEffect, useState, useCallback } from 'react'
import { Trash2, FileText, Calendar } from 'lucide-react'
import { projectsApi, LegacyRaAssessment } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'

interface Props {
  projectSystemId: string
  canDelete: boolean
  onDeleted: () => void
}

export const LegacyRaView: React.FC<Props> = ({ projectSystemId, canDelete, onDeleted }) => {
  const { error: showError, success } = useToast()
  const [data, setData] = useState<LegacyRaAssessment | null>(null)
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const d = await projectsApi.getLegacyAssessment(projectSystemId)
      setData(d)
    } catch (err: any) {
      showError(err.message ?? 'Failed to load legacy assessment')
    } finally {
      setLoading(false)
    }
  }, [projectSystemId, showError])

  useEffect(() => { load() }, [load])

  const handleDelete = async () => {
    if (!data || !confirm('Delete this legacy risk assessment? This cannot be undone.')) return
    setDeleting(true)
    try {
      await projectsApi.deleteLegacyAssessment(projectSystemId)
      success('Legacy risk assessment deleted')
      setData(null)
      onDeleted()
    } catch (err: any) {
      showError(err.message ?? 'Delete failed')
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return <div className="py-12 text-center text-sm text-slate-400">Loading legacy assessment…</div>
  }

  if (!data) {
    return (
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-12 text-center">
        <FileText className="h-10 w-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
        <div className="text-sm font-medium text-slate-700 dark:text-slate-200">No legacy Risk Assessment imported</div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          Drop a legacy IACS Rec.171 workbook anywhere on this page or click <em>Import Risk Assessment</em>.
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-5">

      {/* Section 1: General Information */}
      <Section title="General Information">
        <Grid cols={3}>
          <StatItem label="Company" value={data.company_name} />
          <StatItem label="Target System" value={data.target_system} />
          <StatItem label="Email" value={data.company_email} />
          <StatItem label="Tel" value={data.company_tel} />
        </Grid>
        <SubSection title="Vendor PIC">
          <Grid cols={4}>
            <StatItem label="Name" value={data.vendor_pic_name} />
            <StatItem label="Grade" value={data.vendor_pic_grade} />
            <StatItem label="Email" value={data.vendor_pic_email} />
            <StatItem label="Tel" value={data.vendor_pic_tel} />
          </Grid>
        </SubSection>
        <SubSection title="OnBoard Engineer">
          <Grid cols={4}>
            <StatItem label="Name" value={data.onboard_engineer_name} />
            <StatItem label="Grade" value={data.onboard_engineer_grade} />
            <StatItem label="Email" value={data.onboard_engineer_email} />
            <StatItem label="Tel" value={data.onboard_engineer_tel} />
          </Grid>
        </SubSection>
      </Section>

      {/* Section 2: Risk Assessment header */}
      <Section title="Risk Assessment">
        <Grid cols={4}>
          <StatItem label="Function" value={data.ra_function} />
          <StatItem label="System" value={data.ra_function_of_system} />
          <StatItem label="Vendor" value={data.ra_vendor} />
          <StatItem label="Risk Level" value={data.iacs_risk_level} />
        </Grid>
      </Section>

      {/* Section 3: CBS Interpretation */}
      <Section title="CBS Interpretation">
        <ChipRow>
          <ChipPair label="Computer Based System" value={<OXChip value={data.cbs_interpretation_1} />} />
          <ChipPair label="System Category" value={<CatChip value={data.cbs_interpretation_2} />} />
        </ChipRow>
      </Section>

      {/* Section 4: Acceptance Criteria */}
      <Section title="Acceptance Criteria">
        <Grid cols={5}>
          <ChipPair label="1)" value={<OXChip value={data.ac_1} />} />
          <ChipPair label="2)" value={<OXChip value={data.ac_2} />} />
          <ChipPair label="3)-1" value={<OXChip value={data.ac_3_1} />} />
          <ChipPair label="3)-2" value={<OXChip value={data.ac_3_2} />} />
          <ChipPair label="3)-3" value={<OXChip value={data.ac_3_3} />} />
          <ChipPair label="4)" value={<OXChip value={data.ac_4} />} />
          <ChipPair label="Type Approval" value={<OXChip value={data.type_approval_status} />} />
          <ChipPair label="Additional 1)" value={<OXChip value={data.additional_1} />} />
          <ChipPair label="Additional 2)" value={<OXChip value={data.additional_2} />} />
          <ChipPair label="Additional 3)" value={<OXChip value={data.additional_3} />} />
        </Grid>
      </Section>

      {/* Section 5: IACS Rec.171 */}
      <Section title="IACS Rec.171">
        <Grid cols={5}>
          <StatItem label="Impact" value={data.iacs_impact} />
          <StatItem label="Complexity" value={data.iacs_complexity} />
          <StatItem label="Connectivity" value={data.iacs_connectivity} />
          <StatItem label="Attack Surface" value={data.iacs_attack_surface} />
          <StatItem label="Risk Level" value={data.iacs_risk_level} />
        </Grid>
      </Section>

      {/* Footer */}
      <div className="border-t border-slate-200 dark:border-slate-700 pt-3 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
        <div className="flex items-center gap-3 flex-wrap">
          {data.source_filename && (
            <span className="inline-flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {data.source_filename}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Imported {new Date(data.imported_at).toLocaleDateString()}
            {data.updated_at && data.updated_at !== data.imported_at && (
              <span className="text-slate-400 ml-2">· Updated {new Date(data.updated_at).toLocaleDateString()}</span>
            )}
          </span>
        </div>
        {canDelete && (
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
          >
            <Trash2 className="h-3 w-3" />
            {deleting ? 'Deleting…' : 'Delete legacy RA'}
          </button>
        )}
      </div>
    </div>
  )
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="space-y-2">
    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</h3>
    <div className="space-y-3">{children}</div>
  </section>
)

const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">{title}</div>
    {children}
  </div>
)

const Grid: React.FC<{ cols: 3 | 4 | 5; children: React.ReactNode }> = ({ cols, children }) => {
  const cls = cols === 3
    ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2'
    : cols === 4
    ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2'
    : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2'
  return <div className={cls}>{children}</div>
}

const ChipRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-wrap gap-3">{children}</div>
)

const StatItem: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
  <div className="rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 bg-slate-50 dark:bg-slate-900/40">
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate mt-0.5">
      {value ?? <span className="text-slate-300 dark:text-slate-600 italic">—</span>}
    </div>
  </div>
)

const ChipPair: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 bg-slate-50 dark:bg-slate-900/40 flex items-center gap-2">
    <div className="text-[10px] uppercase tracking-wide text-slate-400 truncate flex-1">{label}</div>
    <div className="flex-shrink-0">{value}</div>
  </div>
)

const OXChip: React.FC<{ value: string | null }> = ({ value }) => {
  const v = (value ?? '').trim().toUpperCase()
  if (v === 'O') {
    return <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">O</span>
  }
  if (v === 'X') {
    return <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400">X</span>
  }
  if (!v) return <span className="text-slate-300 dark:text-slate-600">—</span>
  return <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400">{v}</span>
}

const CatChip: React.FC<{ value: string | null }> = ({ value }) => {
  const v = (value ?? '').trim()
  if (!v) return <span className="text-slate-300 dark:text-slate-600">—</span>
  // CAT I/II/III, escalating severity
  const upper = v.toUpperCase()
  const tone =
    upper.includes('III') ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
    : upper.includes('II') ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
    : upper.includes('I') ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
  return <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${tone}`}>{v}</span>
}
